import React, { useEffect, useRef, useState } from "react";
import { Clock, X } from "lucide-react";

/**
 * TimePicker - a fast quick-select time input built for task scheduling.
 *
 * Props:
 *   value    - "HH:MM" (24h) or "" - same as native <input type="time">
 *   onChange - (newValue: string) => void   // empty string means "cleared"
 *   placeholder - what to show when empty (defaults to "-:-")
 *   testId   - optional data-testid prefix
 *
 * UX:
 *   • Click the field → popover with a grid of 15-min quick-select slots
 *     covering business hours (6 AM – 7 PM).
 *   • One click sets the time and closes the popover.
 *   • A "Type a time…" inline input accepts `09:15`, `9:15`, `9:15am`, `9pm` etc.
 *   • A Clear button wipes the value.
 */
const PRESETS = buildPresets();

function buildPresets() {
  const out = [];
  // 6:00 AM → 7:00 PM inclusive, 15-min steps
  for (let h = 6; h <= 19; h++) {
    for (const m of [0, 15, 30, 45]) {
      out.push({ h, m, value: `${pad(h)}:${pad(m)}` });
    }
  }
  return out;
}

function pad(n) { return String(n).padStart(2, "0"); }

function format12h(value) {
  if (!value) return "";
  const [h, m] = value.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return value;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad(m)} ${ampm}`;
}

function parseLoose(raw) {
  // Accepts "9", "9:15", "9:15am", "21:30", "9 pm", "1430", etc. Returns "HH:MM" or null.
  if (!raw) return null;
  const s = raw.trim().toLowerCase().replace(/\s+/g, "");
  const m = s.match(/^(\d{1,2})[:.]?(\d{0,2})(am|pm)?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  let mm = m[2] ? parseInt(m[2], 10) : 0;
  const mer = m[3];
  if (Number.isNaN(h) || Number.isNaN(mm) || mm > 59) return null;
  if (mer === "pm" && h < 12) h += 12;
  if (mer === "am" && h === 12) h = 0;
  if (h > 23) return null;
  return `${pad(h)}:${pad(mm)}`;
}

export default function TimePicker({ value, onChange, placeholder = "-:-", testId = "time" }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function pick(v) {
    onChange(v);
    setOpen(false);
    setTyped("");
  }
  function commitTyped() {
    const parsed = parseLoose(typed);
    if (parsed) pick(parsed);
  }

  return (
    <div className="relative" ref={ref}>
      <button type="button"
        onClick={() => setOpen(o => !o)}
        className="cesco-input flex items-center justify-between text-left w-full"
        data-testid={`${testId}-trigger`}>
        <span className={value ? "text-gray-900" : "text-gray-400"}>
          {value ? format12h(value) : placeholder}
        </span>
        <Clock size={15} className="text-gray-500 shrink-0 ml-2"/>
      </button>

      {value && (
        <button type="button"
          onClick={(e) => { e.stopPropagation(); onChange(""); }}
          className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
          aria-label="Clear time"
          data-testid={`${testId}-clear`}>
          <X size={13}/>
        </button>
      )}

      {open && (
        <div className="absolute z-40 mt-1 left-0 right-0 sm:w-72 bg-white border rounded-lg shadow-lg p-2"
             data-testid={`${testId}-popover`}>
          {/* Free-type row */}
          <div className="flex gap-2 mb-2">
            <input
              className="cesco-input text-sm flex-1"
              placeholder="Type: 9:15, 2pm, 1430…"
              value={typed}
              onChange={e => setTyped(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commitTyped(); } }}
              data-testid={`${testId}-typed`}
              autoFocus
            />
            <button type="button" className="cesco-btn-ghost text-xs"
                    onClick={commitTyped}
                    disabled={!parseLoose(typed)}
                    data-testid={`${testId}-set`}>Set</button>
          </div>
          {/* Preset grid */}
          <div className="max-h-56 overflow-y-auto grid grid-cols-3 gap-1">
            {PRESETS.map(p => {
              const selected = value === p.value;
              return (
                <button key={p.value} type="button"
                  onClick={() => pick(p.value)}
                  className={
                    "text-xs px-2 py-1.5 rounded border transition-colors " +
                    (selected
                      ? "bg-black text-white border-black"
                      : "bg-white hover:bg-gray-100 border-gray-200 text-gray-800")
                  }
                  data-testid={`${testId}-preset-${p.value.replace(":","")}`}>
                  {format12h(p.value)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
